import { ReactElement, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Input,
  Select,
  Checkbox,
  SearchField,
  Typography,
  PrimaryButton,
  SecondaryButton,
  StatusBar,
  useSnackbar,
  SnackbarStatus,
} from '@weareopenr/component-library';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRoles } from 'src/hooks/useRoles/useRoles';
import { getBrands } from 'src/api/getBrands/getBrands';
import { getUserVenueAccess } from 'src/api/getUserVenueAccess';
import { createUser } from 'src/api/createUser/createUser';
import { updateUser } from 'src/api/updateUser/updateUser';
import { IBrandVenueAccess, IUser } from 'src/types';
import { EMAIL_VALIDATION_REGEX } from 'src/constants/regex';
import { QueryKeys } from 'src/constants/QueryKeys';
import { VENUES_CREATE_PERMISSION } from 'src/constants/permissions';
import { IFormValues } from './types';
import { MAX_INDIVIDUAL_BRANDS, MAX_BRANDS_MESSAGE, WILDCARD_DESELECT_MESSAGE } from './constants';
import { validateUserForm } from './validation';
import { BrandAccessCard } from './BrandAccessCard';
import { AccessSummary } from './AccessSummary';
import styles from './UserModal.module.scss';
import { useUserTracking } from 'src/hooks';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags/useFeatureFlags';

interface IUserFormProps {
  closeModal: () => void;
  user?: IUser;
}

// With venue access management off, the product's choice is full access:
// newly granted brands carry an explicit full grant so users created or
// extended behind the flag stay aligned with the backfill's full-access rows.
// Brands the user already held are left untouched (the API retains their
// existing rows), so a flag-off edit can never widen a specific grant, and
// wildcard access still sends no grants at all.
const withImplicitFullAccess = (data: IFormValues, existingBrands: string[]): IFormValues => {
  const brands = data.brands || [];
  if (brands.includes('*')) return { ...data, venue_access: undefined };

  const alreadyGranted = new Set(existingBrands.includes('*') ? [] : existingBrands);
  const newlyGranted = brands.filter((brandId) => !alreadyGranted.has(brandId));

  return {
    ...data,
    venue_access: newlyGranted.length
      ? newlyGranted.map((brandId) => ({ brand_id: brandId, access: 'full' as const }))
      : undefined,
  };
};

export const UserForm = ({ closeModal, user }: IUserFormProps): ReactElement => {
  const { showSnackbar } = useSnackbar();
  const { trackEvent, captureException } = useUserTracking();
  const { venueAccessManagement } = useFeatureFlags();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBrands, setExpandedBrands] = useState<Set<string>>(
    () => new Set((user?.brands || []).filter((brandId) => brandId !== '*')),
  );
  const isEditing = !!user;

  const {
    control,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<IFormValues>({
    defaultValues: {
      id: user?.id || '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      role: user?.role || '',
      brands: user?.brands || [],
      venue_access: undefined,
    },
    mode: 'onChange',
  });

  const formValues = watch();
  const brands = formValues.brands || [];
  const venueAccess = formValues.venue_access;
  const isAllBrandsSelected = brands.includes('*');

  const { roles, isLoading } = useRoles();
  const { data: brandsData } = useQuery({
    queryKey: [QueryKeys.BRANDS],
    queryFn: getBrands,
    onError: (e: Error) => {
      setErrorMessage(e.message);
      captureException(e);
    },
  });

  useQuery({
    queryKey: [QueryKeys.USER_VENUE_ACCESS, user?.id],
    queryFn: () => getUserVenueAccess(user?.id as string),
    enabled: !!user?.id && venueAccessManagement && !user.brands?.includes('*'),
    onSuccess: (response) => {
      const isWildcard = getValues('brands')?.includes('*');
      if (!isWildcard && !getValues('venue_access')?.length && response.data.length) {
        setValue('venue_access', response.data);
      }
    },
    onError: (e: Error) => {
      setErrorMessage(e.message);
      captureException(e);
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (data: IFormValues): Promise<void> =>
      isEditing ? updateUser(data) : createUser(data),
    onSuccess: (_data, variables) => {
      trackEvent(isEditing ? 'user_updated' : 'user_created', {
        role: variables.role,
        brand_count: variables.brands?.includes('*') ? 'all' : variables.brands?.length || 0,
      });
      showSnackbar({
        message: `User ${isEditing ? 'updated' : 'created'} successfully`,
        status: SnackbarStatus.SUCCESS,
      });
      setErrorMessage(null);
      closeModal();
    },
    onError: (error: Error) => {
      setErrorMessage(`Unable to ${isEditing ? 'update' : 'create'} user, please try later`);
      captureException(error);
    },
  });

  const handleBrandsChange = (updatedIds: string[]) => {
    const hasAllBrands = updatedIds.includes('*');
    const currentHasAllBrands = brands.includes('*');
    let finalValue: string[];

    // Wildcard access is only ever granted by explicitly ticking "All Brands" —
    // enumerating every brand is a distinct grant (it allows venue restrictions
    // and does not cover brands created later).
    if (hasAllBrands && !currentHasAllBrands) {
      finalValue = ['*'];
    } else {
      finalValue = updatedIds.filter((id) => id !== '*');
    }

    const keptAccess = finalValue.includes('*')
      ? []
      : (venueAccess || []).filter((entry) => finalValue.includes(entry.brand_id));
    setValue('venue_access', keptAccess.length ? keptAccess : undefined);
    setValue('brands', finalValue);

    trackEvent('brand_access_changed', {
      brand_count: finalValue.includes('*') ? 'all' : finalValue.length,
    });
  };

  const handleToggleOption = (optionId: string, checked: boolean) => {
    const currentBrandSelection = brands.includes('*')
      ? brandOptions.map((brand) => brand.id)
      : brands;

    if (checked) {
      if (currentBrandSelection.includes(optionId)) return;

      const newSelection = [...currentBrandSelection, optionId];
      // Prevent selecting more than the individual brand cap
      if (
        optionId !== '*' &&
        !newSelection.includes('*') &&
        newSelection.length > MAX_INDIVIDUAL_BRANDS
      ) {
        setError('brands', {
          type: 'manual',
          message: MAX_BRANDS_MESSAGE,
        });
        return;
      }
      clearErrors('brands');
      handleBrandsChange(newSelection);
      if (optionId !== '*') {
        setExpandedBrands((current) => new Set(current).add(optionId));
      }
      return;
    }

    // If deselecting "All Brands", deselect everything
    if (optionId === '*') {
      clearErrors('brands');
      handleBrandsChange([]);
      return;
    }

    // Trimming one brand off the wildcard enumerates the rest, which must fit the cap
    if (isAllBrandsSelected && currentBrandSelection.length - 1 > MAX_INDIVIDUAL_BRANDS) {
      setError('brands', {
        type: 'manual',
        message: WILDCARD_DESELECT_MESSAGE,
      });
      return;
    }

    clearErrors('brands');

    // Remove this option from current selection
    handleBrandsChange(currentBrandSelection.filter((id) => id !== optionId));
    setExpandedBrands((current) => {
      const next = new Set(current);
      next.delete(optionId);
      return next;
    });
  };

  const handleVenueAccessChange = (entry: IBrandVenueAccess) => {
    const others = (venueAccess || []).filter((existing) => existing.brand_id !== entry.brand_id);
    setValue('venue_access', [...others, entry]);
  };

  const isSearching = searchTerm.trim().length > 0;

  const isBrandExpanded = (brandId: string): boolean => isSearching || expandedBrands.has(brandId);

  const handleToggleExpand = (brandId: string) => {
    if (isSearching) return;
    setExpandedBrands((current) => {
      const next = new Set(current);
      if (next.has(brandId)) {
        next.delete(brandId);
      } else {
        next.add(brandId);
      }
      return next;
    });
  };

  const brandOptions =
    brandsData?.map((brand) => ({
      id: brand.brand_id,
      name: brand.name,
    })) || [];

  const visibleBrandOptions = brandOptions.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.trim().toLowerCase()),
  );

  const grantedBrandOptions = brandOptions.filter((option) => brands.includes(option.id));

  // Venue creation needs full venue access on the brand, so a restricted grant
  // silently blocks it. Advisory only, since the combination is still valid.
  const roleCanCreateVenues = !!roles
    .find((roleOption) => roleOption.slug === formValues.role)
    ?.permissions?.includes(VENUES_CREATE_PERMISSION);

  const onSubmit = (data: IFormValues) => {
    mutate(venueAccessManagement ? data : withImplicitFullAccess(data, user?.brands || []));
  };

  return (
    <form className={styles['form']} onSubmit={handleSubmit(onSubmit)}>
      <Typography size="xxlarge" weight="semi-bold" className={styles['heading-text']}>
        {isEditing ? 'Edit User' : 'Create User'}
      </Typography>
      <div className={styles['columns']}>
        <div className={styles['details-column']}>
          <div className={styles['input-field']}>
            <Controller
              name="first_name"
              control={control}
              rules={{
                required: 'Please enter a name',
                validate: (value) => value.trim() !== '' || 'Please enter a name',
              }}
              render={({ field: { ref: _, ...field } }) => (
                <Input
                  {...field}
                  id="firstname"
                  type="text"
                  label="First Name"
                  disabled={isEditing}
                  error={errors.first_name?.message}
                />
              )}
            />
          </div>
          <div className={styles['input-field']}>
            <Controller
              name="last_name"
              control={control}
              rules={{
                required: 'Please enter a name',
                validate: (value) => value.trim() !== '' || 'Please enter a name',
              }}
              render={({ field: { ref: _, ...field } }) => (
                <Input
                  {...field}
                  id="lastname"
                  type="text"
                  label="Last Name"
                  disabled={isEditing}
                  error={errors.last_name?.message}
                />
              )}
            />
          </div>
          <div className={styles['input-field']}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Please enter an email address',
                pattern: {
                  value: EMAIL_VALIDATION_REGEX,
                  message: 'Invalid email address',
                },
              }}
              render={({ field: { ref: _, ...field } }) => (
                <Input
                  {...field}
                  id="email"
                  type="text"
                  label="Email"
                  disabled={isEditing}
                  error={errors.email?.message}
                />
              )}
            />
          </div>
          <div className={styles['input-field']}>
            <Controller
              name="role"
              control={control}
              rules={{
                required: 'Please select a role',
              }}
              render={({ field: { ref: _, ...field } }) => (
                <Select
                  {...field}
                  label="Role"
                  options={roles.map((roleOption) => ({
                    name: roleOption.name,
                    value: roleOption.slug,
                  }))}
                  error={errors.role?.message}
                  loading={isLoading}
                  onChange={(value) => {
                    field.onChange(value);
                    trackEvent('role_selected', { role: value });
                  }}
                />
              )}
            />
          </div>
          <AccessSummary
            isWildcard={isAllBrandsSelected}
            brands={grantedBrandOptions}
            venueAccess={venueAccess}
            venueAccessEnabled={venueAccessManagement}
          />
        </div>
        <div className={styles['brands-column']}>
          <div className={styles['brands-label']}>
            <Typography size="large" weight="semi-bold">
              Brands
            </Typography>
            <Typography
              size="small"
              className={
                !isAllBrandsSelected && brands.length > MAX_INDIVIDUAL_BRANDS
                  ? styles['brands-count-over']
                  : styles['brands-count']
              }
            >
              {isAllBrandsSelected
                ? 'All brands selected'
                : `${brands.length} of ${MAX_INDIVIDUAL_BRANDS} selected`}
            </Typography>
            <SearchField
              className={styles['brands-search']}
              placeholder="Search brands"
              onChange={(event) => setSearchTerm(event.target.value)}
              isDisabled={brandOptions.length === 0}
            />
          </div>
          <div className={styles['all-brands-option']}>
            <Checkbox
              label="All Brands"
              checked={isAllBrandsSelected}
              onChange={(event) => handleToggleOption('*', event.target.checked)}
            />
          </div>
          {venueAccessManagement && (
            <Typography size="small" className={styles['wildcard-note']}>
              Users with access to all brands automatically have full access to every venue. To
              restrict venue access, grant individual brands instead.
            </Typography>
          )}
          {errors.brands && (
            <Typography size="small" className={styles['error-text']}>
              {errors.brands.message}
            </Typography>
          )}
          <div className={styles['brands-container']}>
            {visibleBrandOptions.map((option) => (
              <BrandAccessCard
                key={option.id}
                brandId={option.id}
                brandName={option.name}
                isGranted={brands.includes(option.id)}
                isWildcard={isAllBrandsSelected}
                venueAccessEnabled={venueAccessManagement}
                roleCanCreateVenues={roleCanCreateVenues}
                entry={(venueAccess || []).find((entry) => entry.brand_id === option.id)}
                isExpanded={isBrandExpanded(option.id)}
                onToggleExpand={() => handleToggleExpand(option.id)}
                onToggleGrant={(checked) => handleToggleOption(option.id, checked)}
                onChange={handleVenueAccessChange}
                setErrorMessage={setErrorMessage}
              />
            ))}
          </div>
        </div>
      </div>
      <div className={styles['footer']}>
        <SecondaryButton type="button" onClick={closeModal} disabled={isPending}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="submit"
          loading={isPending}
          disabled={isPending || !validateUserForm(formValues, venueAccessManagement)}
        >
          {isEditing ? 'Save' : 'Create'}
        </PrimaryButton>
      </div>
      {errorMessage && (
        <StatusBar
          className={styles['error-bar']}
          message={errorMessage}
          variant="error"
          onClose={() => setErrorMessage('')}
        />
      )}
    </form>
  );
};
